import {
  type CapiUserDataFragment,
  type CartFragment,
  type CountryCode,
  type ProductOfferAddonFragment,
  type ProductOfferFragment,
} from '@/services/graphql/generated'
import { type EcommerceEvent, initializeGtm, pushToGTMDataLayer, setUserId } from '@/services/gtm'
import { logger } from '@/services/logger/logger.client'
import { datadogRum } from '@datadog/browser-rum'
import { type HighEntropyClientHints } from './clientHints'
import { type InternalEventReporter } from './InternalEventReporter'
import { TrackingEvent } from './TrackingEvent'

type TrackingProductData = {
  id: string
  displayNameFull: string
}

type TrackingOffer = {
  cost: ProductOfferFragment['cost']
  product: {
    id: ProductOfferFragment['product']['id']
    displayNameFull: ProductOfferFragment['product']['displayNameFull']
  }
  variant: {
    typeOfContract: ProductOfferFragment['variant']['typeOfContract']
  }
  addons: Array<ProductOfferAddonFragment>
  priceMatch?: ProductOfferFragment['priceMatch']
}

type ItemListSource = 'store' | 'recommendations'

type TrackingContext = Partial<{
  countryCode: CountryCode
  numberOfPeople: number
  shopSessionId: string
  productId: string
  productDisplayName: string
  partner: string
}>

const trackingLogger = logger.createLogger({ name: 'tracking' })

// Simple version with 2 destinations (GTM and Datadog) implemented inline
export class Tracking {
  static LOGGER_NAME = 'tracking'
  public context: TrackingContext = {}
  public internalEventReporter: InternalEventReporter | null = null

  private logger = trackingLogger

  public reportAppInit = (countryCode: CountryCode) => {
    console.debug(`app init, country=${countryCode}`)
    initializeGtm(countryCode)
  }

  public reportPageView(urlPath: string) {
    const event = {
      event: TrackingEvent.PageView,
      pageData: {
        page: urlPath,
        title: document.title,
      },
    }
    console.debug(event.event, urlPath)
    pushToGTMDataLayer(event)
  }

  public reportExperimentImpression(experimentId: string, variantId: string) {
    const event = {
      event: TrackingEvent.ExperimentImpression,
      eventData: {
        experiment_id: experimentId,
        variant_id: variantId,
      },
      ...Tracking.sessionData(this.context),
      ...Tracking.shopSessionData(this.context),
    }
    console.debug(event.event, variantId)
    pushToGTMDataLayer(event)
  }

  public reportViewProductPage(productData: TrackingProductData) {
    this.reportSelectItem(productData, 'store')
  }

  public reportSelectItem(productData: TrackingProductData, itemListId: string) {
    const event = productDataToEcommerceEvent(TrackingEvent.SelectItem, productData, this.context)
    Object.assign(event.ecommerce, { item_list_id: itemListId })
    this.reportEcommerceEvent(event)
  }

  public reportOpenPriceCalculator(productData: TrackingProductData) {
    this.reportEcommerceEvent(productDataToEcommerceEvent(TrackingEvent.OpenPriceCalculator, productData, this.context))
  }

  public reportViewItem(offer: TrackingOffer, source: ItemListSource) {
    this.reportEcommerceEvent(
      offerToEcommerceEvent({
        event: TrackingEvent.ViewItem,
        offer,
        context: this.context,
        source,
      }),
    )
  }

  public reportAddToCart(offer: TrackingOffer, source: ItemListSource) {
    this.reportEcommerceEvent(
      offerToEcommerceEvent({
        event: TrackingEvent.AddToCart,
        offer: {
          ...offer,
          addons: offer.addons.filter((addon) => addon.isSelected),
        },
        context: this.context,
        source,
      }),
    )
  }

  public reportDeleteFromCart(offer: TrackingOffer) {
    this.reportEcommerceEvent(
      offerToEcommerceEvent({
        event: TrackingEvent.DeleteFromCart,
        offer: {
          ...offer,
          addons: offer.addons.filter((addon) => addon.isSelected),
        },
        context: this.context,
      }),
    )
  }

  public reportViewCart(cart: CartFragment) {
    this.reportEcommerceEvent(cartToEcommerceEvent(TrackingEvent.ViewCart, cart, this.context))
  }

  public reportBeginCheckout(cart: CartFragment) {
    this.reportEcommerceEvent(cartToEcommerceEvent(TrackingEvent.BeginCheckout, cart, this.context))
  }

  public reportPurchase({
    cart,
    memberId,
    capiUserData,
  }: {
    cart: CartFragment
    memberId: string
    capiUserData?: CapiUserDataFragment | null
  }) {
    setUserId(memberId)
    const event = cartToEcommerceEvent(TrackingEvent.Purchase, cart, this.context)
    if (capiUserData) {
      event.ecommerce.userData = {
        em: capiUserData.em,
        ...(capiUserData.gem && { gem: capiUserData.gem }),
        fn: capiUserData.fn,
        ln: capiUserData.ln,
        ad: {
          zp: capiUserData.zp ?? '',
          ct: capiUserData.ct ?? '',
          co: capiUserData.co,
        },
      }
    }
    event.ecommerce.transaction_id = cart.id
    this.reportEcommerceEvent(event)
  }

  private productData(): TrackingProductData {
    return {
      id: this.context.productId ?? '',
      displayNameFull: this.context.productDisplayName ?? '',
    }
  }

  static shopSessionData(context: TrackingContext) {
    return {
      shopSession: { id: context.shopSessionId },
    }
  }

  static sessionData(context: TrackingContext) {
    return {
      sessionData: context.partner
        ? {
            channel: 'widget',
            partner: context.partner,
          }
        : { channel: 'store' },
    }
  }

  private shopSessionData() {
    return Tracking.shopSessionData(this.context)
  }

  private sessionData() {
    return Tracking.sessionData(this.context)
  }

  public reportInsurelyPrompted(productData: TrackingProductData) {
    this.reportEcommerceEvent(productDataToEcommerceEvent(TrackingEvent.InsurelyPrompted, productData, this.context))
  }

  public reportInsurelyAccepted(productData: TrackingProductData) {
    this.reportEcommerceEvent(productDataToEcommerceEvent(TrackingEvent.InsurelyAccepted, productData, this.context))
  }

  public reportInsurelyCorrectlyFetched(productData: TrackingProductData) {
    this.reportEcommerceEvent(
      productDataToEcommerceEvent(TrackingEvent.InsurelyCorrectlyFetched, productData, this.context),
    )
  }

  public reportExternalInsuranceCrossSellShown(data: {
    productName: string
    externalInsurer: string
    priceReduction: number
    externalPrice: number
    hedvigPrice: number
  }) {
    const event = {
      event: TrackingEvent.ExternalInsuranceCrossSellShown,
      eventData: data,
      ...this.shopSessionData(),
      ...this.sessionData(),
    }
    this.logger.info(event.event, event.eventData)
    pushToGTMDataLayer(event)
  }

  public reportExternalInsuranceCrossSellAdded(data: {
    productName: string
    externalInsurer: string
    priceReduction: number
  }) {
    const event = {
      event: TrackingEvent.ExternalInsuranceCrossSellAdded,
      eventData: data,
      ...this.shopSessionData(),
      ...this.sessionData(),
    }
    this.logger.info(event.event, event.eventData)
    pushToGTMDataLayer(event)
  }

  public reportExternalInsuranceCrossSellDismissed(data: { productName: string; externalInsurer: string }) {
    const event = {
      event: TrackingEvent.ExternalInsuranceCrossSellDismissed,
      eventData: data,
      ...this.shopSessionData(),
      ...this.sessionData(),
    }
    this.logger.info(event.event, event.eventData)
    pushToGTMDataLayer(event)
  }

  public reportViewPromotion(options: ViewPromotionOptions) {
    const event = TrackingEvent.ViewPromotion
    const data = {
      ecommerce: {
        promotion_id: options.promotionId,
        creative_name: options.creativeName,
        items: [
          {
            item_id: options.productId,
            item_name: options.productName,
            item_variant: options.productVariant,
            price: options.priceAmount,
          },
        ],
      },
      ...this.shopSessionData(),
      ...this.sessionData(),
    }
    this.reportEcommerceEvent({ event, ...data })
  }

  // Google Analytics ecommerce events
  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm
  private reportEcommerceEvent(ecommerceEvent: EcommerceEvent) {
    const { event, ...dataFields } = ecommerceEvent
    this.logger.info(event, dataFields)
    datadogRum.addAction(event, dataFields)
    pushToGTMDataLayer(ecommerceEvent)
  }

  ////////////////////////////
  // Internal analytics events
  public reportDeviceInfo(deviceInfo: DeviceInfo) {
    this.reportInternalEvent(TrackingEvent.DeviceInfo, deviceInfo)
  }

  public reportExpandPeril(productName: string, perilTitle: string) {
    this.reportInternalEvent(TrackingEvent.ExpandPeril, { productName, perilTitle })
  }

  public reportClickTermsAndConditions(productName: string, productVariant: string) {
    this.reportInternalEvent(TrackingEvent.ClickTermsAndConditions, {
      productName,
      productVariant,
    })
  }

  public reportOpenProductReviews(productName: string) {
    this.reportInternalEvent(TrackingEvent.OpenProductReviews, { productName })
  }

  private reportInternalEvent(type: TrackingEvent, data: Record<string, unknown>) {
    if (this.internalEventReporter == null) return
    this.logger.info(type, data)
    this.internalEventReporter.enqueue(type, data)
  }
}

type DeviceInfo = {
  deviceType: string
  osName: string
  browserName: string
  userAgent: string
  clientHints?: HighEntropyClientHints | null
}

type OfferToEcommerseEventParams = {
  event: TrackingEvent
  offer: TrackingOffer
  context: TrackingContext
  source?: ItemListSource
}

const offerToEcommerceEvent = ({ event, offer, context, source }: OfferToEcommerseEventParams): EcommerceEvent => {
  return {
    event,
    ecommerce: {
      value: offer.cost.net.amount,
      currency: offer.cost.net.currencyCode,
      items: [offerToEcommerceItem(offer, source), ...offer.addons.map(addonToEcommerceItem)],
    },
    price_match: {
      exposure_matched: !!offer.priceMatch,
      price_matched: !!offer.priceMatch && offer.priceMatch.priceReduction.amount > 0,
    },
    ...Tracking.shopSessionData(context),
    ...Tracking.sessionData(context),
  } as const
}

type ViewPromotionOptions = {
  promotionId: string
  creativeName: string
  productId: string
  productName: string
  productVariant?: string
  priceAmount?: number
}

// NOTE: Intentionally not adding coupon field, there's no good mapping between our model and analytics
// Let's figure it out later when/if it becomes a priority
const cartToEcommerceEvent = (event: TrackingEvent, cart: CartFragment, context: TrackingContext): EcommerceEvent => {
  const selectedAddons = cart.items.flatMap((item) => item.productOffer.addons).filter((addon) => addon.isSelected)

  return {
    event,
    ecommerce: {
      value: cart.cost.net.amount,
      currency: cart.cost.net.currencyCode,
      items: [
        ...cart.items.map((item) => offerToEcommerceItem(item.productOffer)),
        ...selectedAddons.map(addonToEcommerceItem),
      ],
    },
    ...Tracking.shopSessionData(context),
    ...Tracking.sessionData(context),
  } as const
}

const productDataToEcommerceEvent = (
  event: TrackingEvent,
  productData: TrackingProductData,
  context: TrackingContext,
): EcommerceEvent => {
  return {
    event,
    ecommerce: {
      items: [
        {
          item_id: productData.id,
          item_name: productData.displayNameFull,
        },
      ],
    },
    ...Tracking.shopSessionData(context),
    ...Tracking.sessionData(context),
  } as const
}

function offerToEcommerceItem(offer: TrackingOffer, source?: ItemListSource) {
  return {
    item_id: offer.product.id,
    item_name: offer.product.displayNameFull,
    price: offer.cost.gross.amount,
    item_variant: offer.variant.typeOfContract,
    ...(source && { item_list_id: source }),
  } as const
}

function addonToEcommerceItem(addon: ProductOfferAddonFragment) {
  const item_name = `${addon.variant.displayName}${addon.coverageText ? ` - ${addon.coverageText}` : ''}`

  return {
    item_id: `Product:${addon.variant.product}`,
    item_name,
    item_variant: addon.variant.product,
    price: addon.grossCost.amount,
  } as const
}
